import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import {
  ArrowLeft, Globe, Shield, CheckCircle, Calendar, Briefcase, Users, MapPin,
  Handshake, BadgeCheck, ExternalLink, Building2, MessageSquare, TrendingUp,
  Sparkles, Award, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const VERIFIED_LABEL = {
  verified: { label: 'Verified', color: 'emerald', icon: BadgeCheck },
  pending: { label: 'Verification pending', color: 'amber', icon: AlertCircle },
  unverified: { label: 'Not yet verified', color: 'slate', icon: Shield },
};

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return null; }
}

function timeOnPlatform(iso) {
  if (!iso) return null;
  try {
    const days = Math.floor((new Date() - new Date(iso)) / 86400000);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} on Mylikeness`;
    if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? '' : 's'} on Mylikeness`;
    return `${Math.floor(days / 365)} year${Math.floor(days / 365) === 1 ? '' : 's'} on Mylikeness`;
  } catch { return null; }
}

export default function AgencyProfileView() {
  const { id: agencyId } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/public/agencies/${agencyId}`)
      .then(r => setAgency(r.data))
      .catch(() => toast.error('Agency profile could not be loaded'))
      .finally(() => setLoading(false));
  }, [agencyId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <div className="max-w-5xl mx-auto px-4 py-10">
          <div className="h-60 bg-white rounded-3xl border border-slate-200 animate-pulse mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="h-32 bg-white rounded-2xl border border-slate-200 animate-pulse" />
            <div className="h-32 bg-white rounded-2xl border border-slate-200 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!agency) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Agency not found</h2>
          <p className="text-slate-500 text-sm mb-5">The profile you were looking for doesn&apos;t exist or you don&apos;t have permission to view it.</p>
          <Button onClick={() => navigate(-1)} variant="outline" data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-2" />Go back</Button>
        </div>
      </div>
    );
  }

  const verifBadge = VERIFIED_LABEL[agency.verified_status] || VERIFIED_LABEL.unverified;
  const VIcon = verifBadge.icon;
  const stats = agency.stats || {};
  const acceptanceRate = stats.acceptance_rate;
  const platformAge = timeOnPlatform(agency.joined_at);

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="agency-profile-view">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} data-testid="agency-profile-back"
          className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-5 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to proposal
        </button>

        {/* ── HERO ── */}
        <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden mb-6">
          {/* Banner stripe */}
          <div className="h-32 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 relative overflow-hidden">
            <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: 'radial-gradient(circle at 25% 50%, white 1px, transparent 1px)',
              backgroundSize: '30px 30px',
            }} />
          </div>

          <div className="px-8 pb-8 -mt-16 relative">
            {/* Logo */}
            <div className="w-28 h-28 rounded-2xl bg-white border-4 border-white shadow-lg overflow-hidden flex items-center justify-center mb-4">
              {agency.logo_url ? (
                <img src={agency.logo_url.startsWith('http') ? agency.logo_url : `${process.env.REACT_APP_BACKEND_URL}${agency.logo_url}`}
                  alt={agency.name} className="w-full h-full object-cover" />
              ) : (
                <Building2 className="w-14 h-14 text-slate-300" />
              )}
            </div>

            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <h1 className="text-3xl font-bold text-slate-900" data-testid="agency-name">{agency.name || 'Unnamed Agency'}</h1>
                  <span data-testid="agency-verified-badge" className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium
                    ${verifBadge.color === 'emerald' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                      verifBadge.color === 'amber'   ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                                                        'bg-slate-50 text-slate-600 border border-slate-200'}`}>
                    <VIcon className="w-3 h-3" /> {verifBadge.label}
                  </span>
                </div>
                {agency.tagline && <p className="text-slate-600 text-base mb-2">{agency.tagline}</p>}
                <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                  {platformAge && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {platformAge}</span>}
                  {agency.team_size && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {agency.team_size} team members</span>}
                  {agency.default_governing_law && (
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {agency.default_governing_law.replace(/_/g, ' ')}</span>
                  )}
                </div>
              </div>

              {agency.website && (
                <a href={agency.website.startsWith('http') ? agency.website : `https://${agency.website}`}
                  target="_blank" rel="noopener noreferrer" data-testid="agency-website-link"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium border border-slate-300 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-colors flex-shrink-0">
                  <Globe className="w-3.5 h-3.5" /> Visit website <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* ── Track-record stats ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Briefcase} label="Campaigns run" value={stats.campaigns_count || 0} color="blue" testid="stat-campaigns" />
          <StatCard icon={Users} label="Creators worked with" value={stats.creators_worked_with || 0} color="purple" testid="stat-creators" />
          <StatCard icon={Handshake} label="Proposals sent" value={stats.proposals_sent || 0} color="teal" testid="stat-proposals-sent" />
          <StatCard
            icon={TrendingUp}
            label="Acceptance rate"
            value={acceptanceRate != null ? `${acceptanceRate}%` : '—'}
            color={acceptanceRate >= 50 ? 'emerald' : acceptanceRate >= 25 ? 'amber' : 'rose'}
            testid="stat-acceptance"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* MAIN — About */}
          <div className="lg:col-span-2 space-y-5">
            <Card title="About" icon={Building2} testid="about-card">
              {agency.about ? (
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{agency.about}</p>
              ) : (
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 text-center">
                  <Building2 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-500">This agency hasn&apos;t added an &ldquo;About&rdquo; section yet.</p>
                </div>
              )}
            </Card>

            {/* Trust signals */}
            <Card title="Trust & verification" icon={Shield} testid="trust-card">
              <div className="space-y-3">
                <TrustRow
                  passed={agency.verified_status === 'verified'}
                  label="Identity verified"
                  desc={agency.verified_status === 'verified'
                    ? 'This agency has confirmed their business identity with Mylikeness.'
                    : 'Verification is pending — proceed with caution and ask for credentials if unsure.'}
                />
                <TrustRow
                  passed={stats.proposals_sent > 0}
                  label="Active on the platform"
                  desc={stats.proposals_sent > 0
                    ? `Has sent ${stats.proposals_sent} proposal${stats.proposals_sent === 1 ? '' : 's'} on Mylikeness.`
                    : 'No proposals sent yet — this could be a brand new agency.'}
                />
                <TrustRow
                  passed={(acceptanceRate || 0) >= 50}
                  label="High creator acceptance"
                  desc={acceptanceRate != null
                    ? `${acceptanceRate}% of creators accept proposals from this agency.`
                    : 'Not enough data yet to compute an acceptance rate.'}
                />
                <TrustRow
                  passed={!!agency.website}
                  label="Public web presence"
                  desc={agency.website
                    ? `Has a verifiable website at ${agency.website.replace(/^https?:\/\//, '')}.`
                    : 'No website on file. You can ask for one in your counter.'}
                />
                <TrustRow
                  passed={!!agency.default_governing_law}
                  label="Clear governing law"
                  desc={agency.default_governing_law
                    ? `Operates under ${agency.default_governing_law.replace(/_/g, ' ')} contracts.`
                    : 'No governing law on file. Confirm jurisdiction before signing.'}
                />
              </div>
            </Card>

            {/* Tips for creators */}
            <Card title="Questions worth asking" icon={MessageSquare} testid="ask-questions-card">
              <p className="text-xs text-slate-500 mb-3">If anything below is unclear, counter the proposal with these clarifying questions:</p>
              <ul className="space-y-2 text-sm text-slate-700">
                {[
                  'What specific brands / products will my likeness be associated with?',
                  'Where will the AI-generated content be published (platforms, regions)?',
                  'How long will the licence last, and what are the renewal terms?',
                  'Can I review and approve every piece of content before it goes live?',
                  'What\u2019s the moral-rights / objection clause if I disagree with the brand direction later?',
                ].map((q, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-blue-500 font-mono text-xs mt-0.5">{String(i + 1).padStart(2, '0')}</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* SIDE — Quick facts + actions */}
          <div className="space-y-5">
            <Card title="At a glance" icon={Award} testid="at-a-glance-card">
              <DefinitionList items={[
                { label: 'Joined Mylikeness', value: fmtDate(agency.joined_at) || 'Unknown' },
                { label: 'Team size', value: agency.team_size || 'Not disclosed' },
                { label: 'Governing law', value: agency.default_governing_law ? agency.default_governing_law.replace(/_/g, ' ') : 'Not disclosed' },
                { label: 'Verification', value: verifBadge.label },
              ]} />
            </Card>

            <Card title="Reach this agency" icon={MessageSquare} testid="contact-card">
              <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                Direct contact is via proposals on Mylikeness — agencies don&apos;t accept off-platform outreach.
              </p>
              <Button onClick={() => navigate(-1)} className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs">
                Back to your proposal
              </Button>
            </Card>

            {/* Safety note */}
            <div className="rounded-2xl bg-amber-50/60 border border-amber-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <p className="text-xs font-semibold text-amber-900">Your protection</p>
              </div>
              <p className="text-[11px] text-amber-700 leading-relaxed">
                Every proposal you accept is governed by your permissions, banned-categories list, and your right to approve all content. Funds are held on-platform until you approve.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Helper components ─── */

function StatCard({ icon: Icon, label, value, color, testid }) {
  const colors = {
    blue:    'from-blue-50 to-blue-100/50 text-blue-600 border-blue-200',
    purple:  'from-purple-50 to-purple-100/50 text-purple-600 border-purple-200',
    teal:    'from-teal-50 to-teal-100/50 text-teal-600 border-teal-200',
    emerald: 'from-emerald-50 to-emerald-100/50 text-emerald-600 border-emerald-200',
    amber:   'from-amber-50 to-amber-100/50 text-amber-600 border-amber-200',
    rose:    'from-rose-50 to-rose-100/50 text-rose-600 border-rose-200',
  };
  return (
    <div data-testid={testid} className={`bg-gradient-to-br ${colors[color] || colors.blue} border rounded-2xl p-4`}>
      <Icon className="w-5 h-5 mb-3 opacity-80" />
      <p className="text-2xl font-bold text-slate-900 leading-none mb-1">{value}</p>
      <p className="text-[11px] text-slate-600 font-medium">{label}</p>
    </div>
  );
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function TrustRow({ passed, label, desc }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50/60 border border-slate-100">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5
        ${passed ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
        {passed ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-slate-800">{label}</p>
        <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{desc}</p>
      </div>
    </div>
  );
}

function DefinitionList({ items }) {
  return (
    <dl className="space-y-3">
      {items.map(({ label, value }) => (
        <div key={label} className="flex justify-between items-baseline gap-3 py-2 border-b border-slate-100 last:border-0">
          <dt className="text-[11px] text-slate-500 font-medium">{label}</dt>
          <dd className="text-xs text-slate-800 font-medium text-right capitalize">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
